import React from 'react';
import { Button, Chip, LinearProgress, Tooltip } from '@mui/material';
import { AlertTriangle, CheckCircle2, Copy, FilePlus2, EyeOff, Info, Layers } from 'lucide-react';
import styles from './KgDryRunReport.module.scss';
import {
  SKIPPED_REPORT_CAP,
  coverageTone,
  formatCoverage,
  formatNumber,
  readDuplicateReport,
  readEmbedRatio,
} from './kgUtils';

/** Duplicate keys listed individually before the rest is summarised. */
const DUPLICATE_GROUPS_SHOWN = 6;

/**
 * The dry-run report, rendered in full.
 *
 * Deliberately NOT a pass/fail. The whole value of dry-run is what it tells you
 * about the shape of the data:
 *
 *   • facet_coverage — a facet at 0 is mapped to a field that is empty. On a real
 *     export, half the columns are typically empty, and finding that here rather
 *     than after a 10M-record load is most of the point.
 *   • unmapped_fields — columns present in the data and named nowhere in the
 *     config. Silent field drift is how a facet quietly stops populating when the
 *     customer adds a column, so each one is one click from being handled.
 *   • duplicate_record_keys — records sharing an upsert key are ONE record, and
 *     a push keeps the last of them. This is silent data loss, so it goes above
 *     everything else.
 *   • preview — what the model will actually receive: facet values as interned
 *     (aliases applied), the parsed date, and the assembled embed text.
 *   • embed_text_chars vs full_text_chars — the embedding-cost preview.
 *
 * `onAddFacet` / `onAddIgnore` are optional: the mapping editor wires them, the
 * create wizard shows the report read-only. `onFixRecordKey` is offered wherever
 * the row identity can actually be changed from.
 */
const KgDryRunReport = ({
  result,
  totalFacets = 0,
  onAddFacet,
  onAddIgnore,
  onFixRecordKey,
  recordKeyLabel,
  compact = false,
}) => {
  if (!result) return null;

  const report = result.report || {};
  const preview = Array.isArray(result.preview) ? result.preview : [];
  const counts = report.counts || {};
  const total = Number(counts.total || 0);
  const coverage = report.facet_coverage || {};
  const unmapped = Array.isArray(report.unmapped_fields) ? report.unmapped_fields : [];
  const warnings = Array.isArray(report.warnings) ? report.warnings : [];
  const skipped = Array.isArray(report.skipped) ? report.skipped : [];
  const duplicates = readDuplicateReport(report);

  const coverageEntries = Object.entries(coverage);
  const zeroCoverage = coverageEntries.filter(([, n]) => Number(n) === 0);

  return (
    <div className={styles.report}>
      {/* ── Headline counts ── */}
      <div className={styles.countsRow}>
        <span className={styles.countChip}>
          <strong>{formatNumber(total)}</strong> sampled
        </span>
        <span className={`${styles.countChip} ${styles.ok}`}>
          <CheckCircle2 size={13} />
          <strong>{formatNumber(counts.ok)}</strong> derived
        </span>
        {duplicates?.unique !== null && duplicates?.unique !== undefined && (
          <Tooltip title="Distinct values of the row identity. Records sharing one are the same record.">
            <span
              className={`${styles.countChip} ${
                duplicates.duplicateCount > 0 ? styles.bad : ''
              }`}
            >
              <Copy size={13} />
              <strong>{formatNumber(duplicates.unique)}</strong> distinct
            </span>
          </Tooltip>
        )}
        {Number(counts.skipped) > 0 && (
          <span className={`${styles.countChip} ${styles.warn}`}>
            <strong>{formatNumber(counts.skipped)}</strong> skipped
          </span>
        )}
        {Number(counts.failed) > 0 && (
          <span className={`${styles.countChip} ${styles.bad}`}>
            <strong>{formatNumber(counts.failed)}</strong> failed
          </span>
        )}
      </div>

      {/* ── Duplicate row identities ── data loss, so it goes first ── */}
      {duplicates && duplicates.duplicateCount > 0 && (
        <div className={styles.calloutDanger}>
          <Layers size={15} />
          <div>
            <strong>
              {formatNumber(duplicates.duplicateCount)} record
              {duplicates.duplicateCount === 1 ? '' : 's'} share a row identity with an earlier
              one
            </strong>
            <p>
              {recordKeyLabel ? (
                <>
                  The identity is <code>{recordKeyLabel}</code>, and these records repeat a value
                  of it.{' '}
                </>
              ) : null}
              Records with the same identity are treated as the same record, and a push keeps the
              LAST one — so loading this as it stands would silently discard{' '}
              {formatNumber(duplicates.duplicateCount)} of them. This is the normal shape of an
              event export, where one entity id appears on many events: add another column to the
              identity until each row is unique.
            </p>
            {duplicates.groups.length > 0 && (
              <ul className={styles.dupList}>
                {duplicates.groups.slice(0, DUPLICATE_GROUPS_SHOWN).map((group) => (
                  <li key={group.key}>
                    <code>{group.key}</code>
                    <span>
                      first seen at input #{group.firstIndex}, repeated at{' '}
                      {group.indexes.slice(0, 5).map((i) => `#${i}`).join(', ')}
                      {group.indexes.length > 5
                        ? ` and ${formatNumber(group.indexes.length - 5)} more`
                        : ''}
                    </span>
                  </li>
                ))}
                {duplicates.groups.length > DUPLICATE_GROUPS_SHOWN && (
                  <li className={styles.dupMore}>
                    …and {formatNumber(duplicates.groups.length - DUPLICATE_GROUPS_SHOWN)} more
                    repeated {duplicates.groups.length - DUPLICATE_GROUPS_SHOWN === 1
                      ? 'identity'
                      : 'identities'}
                  </li>
                )}
              </ul>
            )}
            {onFixRecordKey && (
              <Button size="small" variant="text" onClick={onFixRecordKey}>
                Fix the row identity
              </Button>
            )}
          </div>
        </div>
      )}

      {/* ── Warnings ── never folded away.
          This is where "this facet is 2% populated", "no title column", "nothing
          is embedded" and the duplicate-key note arrive. Collapsed, they are the
          part of the report nobody reads and every one of them is something the
          author would want to know BEFORE the data goes in. ── */}
      {warnings.length > 0 && (
        <div className={styles.calloutWarn}>
          <AlertTriangle size={15} />
          <div>
            <strong>
              {warnings.length} thing{warnings.length === 1 ? '' : 's'} worth knowing about this
              mapping
            </strong>
            <ul className={styles.warnList}>
              {warnings.map((w, i) => (
                <li key={i}>{String(w)}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* ── Facet coverage ── the single most useful signal in the product ── */}
      <section className={styles.section}>
        <h4 className={styles.sectionTitle}>
          Facet coverage
          <span className={styles.sectionHint}>
            how many of the {formatNumber(total)} sampled records carry each facet
          </span>
        </h4>

        {coverageEntries.length === 0 ? (
          <div className={styles.calloutDanger}>
            <AlertTriangle size={15} />
            <div>
              <strong>No facets — the agent can filter and count nothing</strong>
              <p>
                Facets are what make “how many” and “which ones” answerable exactly. Without
                any, this dataset behaves like ordinary document search. Add at least one from
                the columns listed below.
              </p>
            </div>
          </div>
        ) : (
          <>
            {zeroCoverage.length > 0 && (
              <div className={styles.calloutDanger}>
                <AlertTriangle size={15} />
                <div>
                  <strong>
                    {zeroCoverage.length} facet{zeroCoverage.length === 1 ? '' : 's'} never
                    populated
                  </strong>
                  <p>
                    {zeroCoverage.map(([name]) => name).join(', ')} — the column is empty in this
                    sample, or the path doesn’t resolve. Check the column name, and remember a
                    name with a space, hyphen or dot has to be quoted with backticks.
                  </p>
                </div>
              </div>
            )}

            <ul className={styles.coverageList}>
              {coverageEntries.map(([name, count]) => {
                const pct = total ? (Number(count) / total) * 100 : 0;
                return (
                  <li key={name} className={styles.coverageRow}>
                    <span className={styles.coverageName}>{name}</span>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, pct)}
                      className={`${styles.coverageBar} ${styles[coverageTone(pct)]}`}
                    />
                    <span className={styles.coverageValue}>
                      {formatNumber(count)} / {formatNumber(total)}
                      <em>{formatCoverage(pct)}</em>
                    </span>
                  </li>
                );
              })}
            </ul>
            {totalFacets > coverageEntries.length && (
              <p className={styles.muted}>
                {totalFacets - coverageEntries.length} facet
                {totalFacets - coverageEntries.length === 1 ? '' : 's'} produced no values at all
                in this sample.
              </p>
            )}
          </>
        )}
      </section>

      {/* ── Unmapped fields ── */}
      {unmapped.length > 0 && (
        <section className={styles.section}>
          <h4 className={styles.sectionTitle}>
            Columns not in the mapping
            <span className={styles.sectionHint}>
              present in the data, named nowhere in the config
            </span>
          </h4>
          <div className={styles.unmappedList}>
            {unmapped.map((field) => (
              <div key={field} className={styles.unmappedRow}>
                <code>{field}</code>
                {(onAddFacet || onAddIgnore) && (
                  <span className={styles.unmappedActions}>
                    {onAddFacet && (
                      <Tooltip title="Make this filterable and countable">
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<FilePlus2 size={13} />}
                          onClick={() => onAddFacet(field)}
                        >
                          Add as facet
                        </Button>
                      </Tooltip>
                    )}
                    {onAddIgnore && (
                      <Tooltip title="Known junk — stop warning about it">
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<EyeOff size={13} />}
                          onClick={() => onAddIgnore(field)}
                        >
                          Ignore
                        </Button>
                      </Tooltip>
                    )}
                  </span>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Skipped ── */}
      {skipped.length > 0 && (
        <section className={styles.section}>
          <h4 className={styles.sectionTitle}>
            Skipped records
            {skipped.length >= SKIPPED_REPORT_CAP && (
              <span className={styles.sectionHint}>
                first {SKIPPED_REPORT_CAP} only — there may be more
              </span>
            )}
          </h4>
          <ul className={styles.plainList}>
            {skipped.slice(0, compact ? 5 : 25).map((s, i) => (
              <li key={i}>
                <code>{s.what}</code> — {s.why}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ── Preview: what the model actually receives ── */}
      {preview.length > 0 && (
        <section className={styles.section}>
          <h4 className={styles.sectionTitle}>
            Derived preview
            <span className={styles.sectionHint}>what the agent will see for these records</span>
          </h4>
          {preview.slice(0, compact ? 1 : 3).map((item, index) => {
            if (item?.error) {
              return (
                <div key={index} className={styles.calloutDanger}>
                  <AlertTriangle size={15} />
                  <div>
                    <strong>This record could not be derived</strong>
                    <p>{item.error}</p>
                  </div>
                </div>
              );
            }
            // Denominator is `full_text_chars` — the document's actual length.
            // `total_text_chars` is a read-tier planner input rather than a length,
            // and dividing by it produced ratios over 100% on records where
            // everything was in fact embedded.
            const { embedded: embedChars, full: totalChars, pct: ratio, allEmbedded } =
              readEmbedRatio(item);
            const normalized = item.normalized;
            return (
              <div key={item.record_key || index} className={styles.previewCard}>
                <div className={styles.previewHead}>
                  <code className={styles.previewKey}>{item.record_key}</code>
                  {item.event_at && (
                    <span className={styles.previewDate}>
                      {String(item.event_at).slice(0, 10)}
                      {item.event_ym ? ` · ${item.event_ym}` : ''}
                    </span>
                  )}
                </div>
                <div className={styles.previewTitle}>{item.title || <em>no title</em>}</div>

                {item.facets && Object.keys(item.facets).length > 0 && (
                  <div className={styles.previewFacets}>
                    {Object.entries(item.facets).map(([name, values]) =>
                      (Array.isArray(values) ? values : [values]).map((v, i) => (
                        <Chip
                          key={`${name}-${i}`}
                          size="small"
                          label={`${name}: ${v}`}
                          className={styles.facetChip}
                        />
                      ))
                    )}
                  </div>
                )}

                {/* What input normalization produced, when it is on. Shown
                    because a derived record_key or title is otherwise invisible
                    until something is already wrong with it. */}
                {normalized && Object.keys(normalized).length > 0 && (
                  <div className={styles.normalizedBlock}>
                    <span className={styles.normalizedHead}>
                      <Layers size={12} />
                      Derived by input normalization
                      {item.normalized_preview_is_abbreviated && (
                        <em className={styles.normalizedTrunc}>shortened for this preview</em>
                      )}
                    </span>
                    <div className={styles.normalizedGrid}>
                      {Object.entries(normalized).map(([name, value]) => (
                        <React.Fragment key={name}>
                          <code className={styles.normalizedKey}>{name}</code>
                          <span className={styles.normalizedValue}>
                            {value === null || value === undefined || value === '' ? (
                              <em>empty</em>
                            ) : (
                              String(typeof value === 'object' ? JSON.stringify(value) : value)
                            )}
                          </span>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                )}

                {item.embed_text_preview && (
                  <pre className={styles.previewText}>{item.embed_text_preview}</pre>
                )}

                {/* The ratio is the embedding-cost preview: what fraction of the
                    record's text is actually being vectorised. */}
                {totalChars > 0 && (
                  <div className={styles.previewCost}>
                    <Info size={12} />
                    {allEmbedded ? (
                      <>
                        All of this record’s text is searchable by meaning (
                        {formatNumber(totalChars)} characters).
                      </>
                    ) : (
                      <>
                        {formatNumber(embedChars)} of {formatNumber(totalChars)} characters
                        embedded ({ratio}%) — the rest is stored and readable, just not
                        searchable by meaning.
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </section>
      )}
    </div>
  );
};

export default KgDryRunReport;
